import { useEffect, useRef, useState } from "react";
import { Button } from "@jurisui/core/primitives/button";
import { RecipeDiagram } from "./RecipeDiagram";
import {
  DIAGRAM_KINDS,
  DIAGRAM_KIND_LABELS,
  type DiagramGenerationRequest,
  type DiagramKind,
  type DiagramRecipe,
  type DiagramFocus,
} from "../diagram-types";
import type {
  ArchitectureContext,
  ArchitectureSnapshot,
} from "../architecture-model";

export function DiagramExplorer({
  enabled,
  snapshot,
  context,
  initialDiagram,
  onGenerate,
  onFocus,
  onSave,
  onDiscuss,
  onCompare,
}: {
  enabled: boolean;
  snapshot: ArchitectureSnapshot;
  context: ArchitectureContext;
  initialDiagram?: DiagramRecipe;
  onGenerate: (request: DiagramGenerationRequest) => Promise<DiagramRecipe>;
  onFocus: (focus: DiagramFocus, label: string, kind: DiagramKind, displayLevel?: 'overview' | 'technical') => void;
  onSave: (diagram: DiagramRecipe) => void | Promise<void>;
  onDiscuss: (diagram: DiagramRecipe) => void;
  onCompare: (diagram: DiagramRecipe) => void;
}) {
  const requestRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      ++requestRef.current;
    };
  }, []);
  const [diagram, setDiagram] = useState(initialDiagram);
  const [kind, setKind] = useState<DiagramKind>(
    initialDiagram?.kind ?? "network",
  );
  const [displayLevel, setDisplayLevel] = useState<'overview' | 'technical' | undefined>(initialDiagram ? initialDiagram.displayLevel : 'technical');
  const [trail, setTrail] = useState<
    Array<{ focus: DiagramFocus; label: string }>
  >([{ focus: {}, label: "Overview" }]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const current = trail.at(-1)!;
  const historical = Boolean(
    diagram &&
    (diagram.catalog.phase !== context.phase ||
      diagram.catalog.sha256 !== snapshot.sha256),
  );
  async function build(
    focus: DiagramFocus,
    label: string,
    nextKind = kind,
    nextTrail = [...trail, { focus, label }],
    nextDisplayLevel = displayLevel,
  ) {
    const requestId = ++requestRef.current;
    const matchingFlows = (snapshot.catalog.flowPresets ?? []).filter(
      (flow) =>
        flow.phase === context.phase &&
        (!focus.entityId || flow.entityIds.includes(focus.entityId)),
    );
    if (
      nextKind === "sequence" &&
      !focus.flowId &&
      matchingFlows.length !== 1
    ) {
      setKind(nextKind);
      setError("Choose a documented flow to open its sequence.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const result = await onGenerate({
        architecture: {
          ...context,
          selectedEntityIds: [],
          selectedRelationshipIds: [],
          filters: {},
        },
        kind: nextKind,
        ...(nextDisplayLevel ? { displayLevel: nextDisplayLevel } : {}),
        direction: "LR",
        scope: "connected",
        selectedEntityIds: [],
        focus,
        title: `${label} · ${DIAGRAM_KIND_LABELS[nextKind]}`,
      });
      if (!mountedRef.current || requestId !== requestRef.current) return;
      const resolvedFocus =
        (result.metadata?.exploration as { focus?: DiagramFocus } | undefined)
          ?.focus ?? focus;
      setDiagram(result);
      setDisplayLevel(nextDisplayLevel);
      setTrail(
        nextTrail.map((entry, index) =>
          index === nextTrail.length - 1
            ? { ...entry, focus: resolvedFocus }
            : entry,
        ),
      );
      onFocus(resolvedFocus, label, nextKind, nextDisplayLevel);
    } catch (caught) {
      if (!mountedRef.current || requestId !== requestRef.current) return;
      setError(
        caught instanceof Error
          ? caught.message
          : "The diagram could not be opened.",
      );
    } finally {
      if (mountedRef.current && requestId === requestRef.current)
        setBusy(false);
    }
  }
  useEffect(() => {
    if (
      !initialDiagram &&
      enabled &&
      (!diagram ||
        diagram.catalog.phase !== context.phase ||
        diagram.catalog.sha256 !== snapshot.sha256)
    ) {
      const supported =
        !current.focus.entityId ||
        snapshot.catalog.entities.some(
          (entity) =>
            entity.id === current.focus.entityId &&
            entity.placements[context.phase],
        );
      const flowSupported =
        !current.focus.flowId ||
        snapshot.catalog.flowPresets?.some(
          (flow) =>
            flow.id === current.focus.flowId && flow.phase === context.phase,
        );
      const next: DiagramFocus = supported
        ? {
            ...current.focus,
            flowId: flowSupported ? current.focus.flowId : undefined,
            offset: 0,
          }
        : {};
      void build(
        next,
        supported ? current.label : "Overview",
        kind,
        supported ? trail : [{ focus: {}, label: "Overview" }],
      );
    }
    return () => {
      ++requestRef.current;
      setBusy(false);
    };
  }, [context.phase, snapshot.sha256, enabled]);
  function drill(id: string) {
    if (!diagram || historical) return;
    const node = diagram.nodes.find((entry) => entry.id === id);
    const entityId =
      node?.entityId ??
      (snapshot.catalog.entities.some((entity) => entity.id === id)
        ? id
        : undefined);
    const boundaryId = snapshot.catalog.boundaries?.find(
      (boundary) => boundary.id === id,
    )?.id;
    if (entityId && current.focus.entityId === entityId) return;
    if (boundaryId && current.focus.boundaryId === boundaryId) return;
    if (entityId)
      void build(
        {
          entityId,
          ...(current.focus.flowId &&
          snapshot.catalog.flowPresets?.some(
            (flow) =>
              flow.id === current.focus.flowId &&
              flow.entityIds.includes(entityId),
          )
            ? { flowId: current.focus.flowId }
            : {}),
        },
        node?.label ??
          snapshot.catalog.entities.find((entity) => entity.id === entityId)
            ?.name ??
          entityId,
      );
    else if (boundaryId) void build({ boundaryId }, node?.label ?? boundaryId);
  }
  const exploration = (
    diagram?.metadata as
      | {
          exploration?: {
            nextOffset?: number;
            previousOffset?: number;
            totalNodes?: number;
            leaf?: boolean;
          };
        }
      | undefined
  )?.exploration;
  const flows = (snapshot.catalog.flowPresets ?? []).filter(
    (flow) =>
      flow.phase === context.phase &&
      (!current.focus.entityId ||
        flow.entityIds.includes(current.focus.entityId)),
  );
  const entity = snapshot.catalog.entities.find(
    (entry) => entry.id === current.focus.entityId,
  );
  return (
    <section
      className="diagram-explorer"
      aria-label="Diagram explorer"
      aria-busy={busy}
    >
      <nav aria-label="Diagram breadcrumbs">
        {trail.length > 1 && (
          <Button
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() => {
              const next = trail.slice(0, -1);
              const previous = next.at(-1)!;
              void build(previous.focus, previous.label, kind, next);
            }}
          >
            Back
          </Button>
        )}
        {trail.map((entry, index) => (
          <Button
            key={`${index}-${entry.label}`}
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() =>
              void build(
                entry.focus,
                entry.label,
                kind,
                trail.slice(0, index + 1),
              )
            }
          >
            {entry.label}
          </Button>
        ))}
      </nav>
      <div className="diagram-tools">
        <label>Detail level <select aria-label="Diagram detail level" value={displayLevel ?? 'overview'} disabled={busy} onChange={event => void build(current.focus, current.label, kind, trail, event.target.value as 'overview' | 'technical')}>
          <option value="overview">Overview</option><option value="technical">Technical</option>
        </select></label>
        {(kind === "sequence" || kind === "data-flow") && (
          <label>
            Flow{" "}
            <select
              aria-label="Architecture flow"
              value={current.focus.flowId ?? ""}
              disabled={busy}
              onChange={(event) =>
                void build(
                  {
                    ...current.focus,
                    flowId: event.target.value || undefined,
                    offset: 0,
                  },
                  current.label,
                  kind,
                  trail.slice(0, -1).concat({
                    focus: {
                      ...current.focus,
                      flowId: event.target.value || undefined,
                    },
                    label: current.label,
                  }),
                )
              }
            >
              <option value="">Choose a documented flow</option>
              {flows.map((flow) => (
                <option key={flow.id} value={flow.id}>
                  {flow.title}
                </option>
              ))}
            </select>
          </label>
        )}
        <label>
          Diagram type{" "}
          <select
            aria-label="Diagram type"
            value={kind}
            disabled={busy}
            onChange={(event) => {
              const next = event.target.value as DiagramKind;
              setKind(next);
              void build(current.focus, current.label, next, trail, next === 'network' || next === 'deployment' ? 'technical' : 'overview');
            }}
          >
            {DIAGRAM_KINDS.map((value) => (
              <option key={value} value={value}>
                {DIAGRAM_KIND_LABELS[value]}
              </option>
            ))}
          </select>
        </label>
        {diagram && (
          <Button variant="ghost" size="sm" onClick={() => onCompare(diagram)}>
            Open comparison tab
          </Button>
        )}
      </div>
      {exploration && (
        <div className="diagram-pages" aria-label="Diagram pages">
          {exploration.previousOffset !== undefined && (
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() =>
                void build(
                  { ...current.focus, offset: exploration.previousOffset },
                  current.label,
                  kind,
                  trail,
                )
              }
            >
              Previous
            </Button>
          )}
          {exploration.nextOffset !== undefined && (
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() =>
                void build(
                  { ...current.focus, offset: exploration.nextOffset },
                  current.label,
                  kind,
                  trail,
                )
              }
            >
              More components
            </Button>
          )}
        </div>
      )}
      {error && <p role="alert">{error}</p>}
      {busy && <p role="status">Opening {current.label}…</p>}
      {historical && (
        <p className="answer-scope">
          Saved diagram · {diagram?.catalog.phase} · catalogue{" "}
          {diagram?.catalog.revision}. Open Overview to explore the current
          phase.
        </p>
      )}
      {diagram && (
        <>
          <RecipeDiagram
            sourceLabels={Object.fromEntries(snapshot.catalog.sources.filter(source => source.enabled !== false).map(source => [source.id, source.title]))}
            diagram={diagram}
            onSave={onSave}
            onNodeSelect={(id) => drill(id)}
          />
          <Button variant="ghost" size="sm" onClick={() => onDiscuss(diagram)}>
            Discuss saved diagram
          </Button>
        </>
      )}
      {entity && (
        <aside className="entity-detail">
          <h3>{entity.name}</h3>
          <p>{entity.phaseNarratives?.[context.phase] ?? entity.summary}</p>
          <p>
            {entity.placements[context.phase]?.runtime} ·{" "}
            {entity.placements[context.phase]?.location} ·{" "}
            {entity.placements[context.phase]?.evidence}
          </p>
          {exploration?.leaf && (
            <p>No deeper sourced detail is available for this diagram type.</p>
          )}
          <ul>
            {entity.sourceRefs
              .filter((ref) =>
                snapshot.catalog.sources.some(
                  (source) =>
                    source.id === ref.sourceId && source.enabled !== false,
                ),
              )
              .map((ref, index) => (
                <li key={index}>
                  {
                    snapshot.catalog.sources.find(
                      (source) => source.id === ref.sourceId,
                    )?.title
                  }
                  {ref.slide ? ` · slide ${ref.slide}` : ""}
                  {ref.section ? ` · ${ref.section}` : ""}
                </li>
              ))}
          </ul>
        </aside>
      )}
    </section>
  );
}
